import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import ImageUpload from '../../../components/ui/ImageUpload';
import { featuredContentService } from '../../../services/featuredContentService';

const FeaturedContentManagement = () => {
  const [featuredItems, setFeaturedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({
    title: '',
    subtitle: '',
    image_path: '',
    is_active: true,
    display_order: 1
  });

  useEffect(() => {
    loadFeaturedContent();
  }, []);

  const loadFeaturedContent = async () => {
    try {
      setLoading(true);
      const data = await featuredContentService?.getFeaturedContent();
      setFeaturedItems(data || []);
    } catch (error) {
      console.error('Error loading featured content:', error);
      setError('Failed to load featured content');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveItem = async (itemData, isNew = false) => {
    try {
      setSaving(true);
      setError('');
      
      if (isNew) {
        await featuredContentService?.createFeaturedContent(itemData);
        setSuccess('Featured content added successfully');
        setShowAddForm(false);
        setNewItem({
          title: '',
          subtitle: '',
          image_path: '',
          is_active: true,
          display_order: Math.max(...featuredItems?.map(item => item?.display_order), 0) + 1
        });
      } else {
        await featuredContentService?.updateFeaturedContent(itemData?.id, itemData);
        setSuccess('Featured content updated successfully');
        setEditingItem(null);
      }
      
      setTimeout(() => setSuccess(''), 3000);
      await loadFeaturedContent();
    } catch (error) {
      console.error('Error saving featured content:', error);
      setError('Failed to save featured content');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (id) => {
    if (!confirm('Are you sure you want to delete this featured content?')) {
      return;
    }
    
    try {
      setSaving(true);
      await featuredContentService?.deleteFeaturedContent(id);
      setSuccess('Featured content deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
      await loadFeaturedContent();
    } catch (error) {
      console.error('Error deleting featured content:', error);
      setError('Failed to delete featured content');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (item) => {
    try {
      setSaving(true);
      const updatedItem = { ...item, is_active: !item?.is_active };
      await featuredContentService?.updateFeaturedContent(item?.id, updatedItem);
      setSuccess(`Featured content ${updatedItem?.is_active ? 'activated' : 'deactivated'}`);
      setTimeout(() => setSuccess(''), 3000);
      await loadFeaturedContent();
    } catch (error) {
      console.error('Error toggling active status:', error);
      setError('Failed to update active status');
    } finally {
      setSaving(false);
    }
  };

  const handleReorder = async (dragIndex, dropIndex) => {
    try {
      setSaving(true);
      const reorderedItems = [...featuredItems];
      const draggedItem = reorderedItems?.[dragIndex];
      reorderedItems?.splice(dragIndex, 1);
      reorderedItems?.splice(dropIndex, 0, draggedItem);
      
      // Update display orders
      const updates = reorderedItems?.map((item, index) => ({
        ...item,
        display_order: index + 1
      }));
      
      await featuredContentService?.reorderFeaturedContent(updates);
      setSuccess('Display order updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      await loadFeaturedContent();
    } catch (error) {
      console.error('Error reordering items:', error);
      setError('Failed to reorder items');
    } finally {
      setSaving(false);
    }
  };

  const FeaturedItemForm = ({ item, onSave, onCancel, isNew = false }) => {
    const [formData, setFormData] = useState(item || newItem);

    const handleSubmit = (e) => {
      e?.preventDefault();
      if (!formData?.title?.trim() || !formData?.subtitle?.trim()) {
        setError('Title and subtitle are required');
        return;
      }
      onSave(formData, isNew);
    };

    const handleInputChange = (field, value) => {
      setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleImageUpload = (imageData) => {
      setFormData(prev => ({ ...prev, image_path: imageData?.url }));
    };

    const handleImageRemove = () => {
      setFormData(prev => ({ ...prev, image_path: '' }));
    };

    return (
      <div className="bg-muted/50 border border-border rounded-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Title <span className="text-destructive">*</span>
              </label>
              <Input
                type="text"
                value={formData?.title}
                onChange={(e) => handleInputChange('title', e?.target?.value)}
                placeholder="Your Passion / Precisely Tracked"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Display Order
              </label>
              <Input
                type="number"
                value={formData?.display_order}
                onChange={(e) => handleInputChange('display_order', parseInt(e?.target?.value) || 1)}
                min="1"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Subtitle <span className="text-destructive">*</span>
            </label>
            <Input
              type="text"
              value={formData?.subtitle}
              onChange={(e) => handleInputChange('subtitle', e?.target?.value)}
              placeholder="Turn your obsession into a sophisticated portfolio"
              required
            />
          </div>

          <ImageUpload
            currentImage={formData?.image_path ? { url: formData?.image_path, path: null } : null}
            onImageUpload={handleImageUpload}
            onImageRemove={handleImageRemove}
            label="Background Image"
            folder="featured-content"
            disabled={saving}
          />
          
          <p className="text-xs text-muted-foreground">
            Background images will be displayed with a dark overlay for better text readability. 
            Leave empty to use the default gradient background.
          </p>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id={`active-${isNew ? 'new' : item?.id}`}
              checked={formData?.is_active}
              onChange={(e) => handleInputChange('is_active', e?.target?.checked)}
              className="rounded border-border text-primary focus:ring-primary"
            />
            <label htmlFor={`active-${isNew ? 'new' : item?.id}`} className="text-sm text-foreground">
              Active (will be shown in rotation)
            </label>
          </div>

          <div className="flex items-center justify-end space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={saving}
            >
              {isNew ? 'Add Featured Content' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        <p className="text-muted-foreground mt-4">Loading featured content...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Featured Homepage Content</h3>
          <p className="text-sm text-muted-foreground">
            Manage the rotating text and background images on the homepage hero section
          </p>
        </div>
        <Button
          onClick={() => setShowAddForm(true)}
          disabled={showAddForm}
        >
          <Icon name="Plus" size={16} className="mr-2" />
          Add Featured Content
        </Button>
      </div>
      {/* Add New Item Form */}
      {showAddForm && (
        <FeaturedItemForm
          item={newItem}
          onSave={handleSaveItem}
          onCancel={() => setShowAddForm(false)}
          isNew={true}
        />
      )}
      {/* Current Featured Items */}
      <div className="bg-card border border-border rounded-lg">
        {featuredItems?.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="Image" className="text-muted-foreground mx-auto mb-4" size={48} />
            <h3 className="text-lg font-medium text-foreground mb-2">No featured content</h3>
            <p className="text-muted-foreground mb-4">
              Add your first featured content to display on the homepage
            </p>
            <Button onClick={() => setShowAddForm(true)}>
              <Icon name="Plus" size={16} className="mr-2" />
              Add Featured Content
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {featuredItems?.sort((a, b) => a?.display_order - b?.display_order)?.map((item, index) => (
              <div key={item?.id} className="p-6">
                {editingItem?.id === item?.id ? (
                  <FeaturedItemForm
                    item={editingItem}
                    onSave={handleSaveItem}
                    onCancel={() => setEditingItem(null)}
                  />
                ) : (
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-primary/10 text-primary rounded-full text-sm font-medium">
                          {item?.display_order}
                        </span>
                        <div>
                          <h4 className="text-lg font-medium text-foreground">
                            {item?.title}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {item?.subtitle}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            item?.is_active 
                              ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                          }`}>
                            {item?.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                      
                      {item?.image_path && (
                        <div className="ml-11 mt-2">
                          <div className="flex items-center space-x-3">
                            <img 
                              src={item?.image_path} 
                              alt="Background preview" 
                              className="w-20 h-12 object-cover rounded border"
                            />
                            <div>
                              <p className="text-xs font-medium text-foreground">Custom Background</p>
                              <p className="text-xs text-muted-foreground truncate max-w-xs">
                                {item?.image_path}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleReorder(index, Math.max(0, index - 1))}
                          disabled={index === 0 || saving}
                          className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Icon name="ChevronUp" size={16} />
                        </button>
                        <button
                          onClick={() => handleReorder(index, Math.min(featuredItems?.length - 1, index + 1))}
                          disabled={index === featuredItems?.length - 1 || saving}
                          className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Icon name="ChevronDown" size={16} />
                        </button>
                      </div>
                      
                      <button
                        onClick={() => handleToggleActive(item)}
                        disabled={saving}
                        className={`p-2 rounded-lg transition-colors ${
                          item?.is_active 
                            ? 'text-warning hover:bg-warning/10' : 'text-success hover:bg-success/10'
                        }`}
                      >
                        <Icon name={item?.is_active ? 'EyeOff' : 'Eye'} size={16} />
                      </button>
                      
                      <button
                        onClick={() => setEditingItem(item)}
                        disabled={saving}
                        className="p-2 text-primary hover:bg-muted rounded-lg transition-colors"
                      >
                        <Icon name="Edit" size={16} />
                      </button>
                      
                      <button
                        onClick={() => handleDeleteItem(item?.id)}
                        disabled={saving}
                        className="p-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                      >
                        <Icon name="Trash2" size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Enhanced Preview Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-lg font-semibold text-foreground mb-4">Live Preview</h4>
        <div className="relative rounded-lg overflow-hidden" style={{ height: '300px' }}>
          {/* Background Image or Gradient */}
          <div className="absolute inset-0">
            {featuredItems?.find(item => item?.is_active)?.image_path ? (
              <div 
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{
                  backgroundImage: `url(${featuredItems?.find(item => item?.is_active)?.image_path})`,
                  filter: 'grayscale(100%) contrast(1.1) brightness(0.3)'
                }}
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black" />
            )}
            <div className="absolute inset-0 bg-black/40" />
          </div>
          
          {/* Content */}
          <div className="relative z-10 h-full flex items-center justify-center text-white text-center px-8">
            <div>
              <div className="text-3xl md:text-4xl font-bold mb-4">
                {featuredItems?.find(item => item?.is_active)?.title || 'No Active Content'}
              </div>
              <div className="text-lg text-gray-300">
                {featuredItems?.find(item => item?.is_active)?.subtitle || 'Add featured content to see preview'}
              </div>
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          This shows how the first active featured content will appear on the homepage with background image support
        </p>
      </div>
      {/* Alerts */}
      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" className="text-destructive" size={16} />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        </div>
      )}
      {success && (
        <div className="p-4 bg-success/10 border border-success/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="CheckCircle" className="text-success" size={16} />
            <p className="text-sm text-success">{success}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeaturedContentManagement;